/**
 * Structure3DPreview — V14.76 (inspiration Homebyme).
 *
 * Vue 3D temps réel de la structure EN COURS D'ÉDITION (non sauvegardée
 * nécessaire) : murs extrudés (épaisseur + HSP + soupente), menuiseries,
 * escaliers, terrain. Rendu via le Shoebox3DViewer vanilla Three.js.
 */
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Rotate3d, Boxes, Camera, Loader2 } from 'lucide-react';
import Shoebox3DViewer from '../../3d/Shoebox3DViewer';
import { createDeterministicWhitebox } from '../DeterministicWhiteboxButton';
import { usePlanEditor } from './PlanEditorContext';
import { useImageDims } from './useImageDims';

const clamp01 = (v) => Math.max(0, Math.min(1, v));

const Structure3DPreview = ({ bgImageUrl, hspM = 2.5, projectId = null, lotId = null }) => {
  const { walls, openings, stairs, fixtures, terrainPolygon, calibration } = usePlanEditor();
  const dims = useImageDims(bgImageUrl);
  // V15.01 — mode maquette blanche + capture haute qualité (Three.js) → référence d'étape 1.
  const [whitebox, setWhitebox] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const apiRef = useRef(null);
  const captureWhitebox = async () => {
    if (!apiRef.current || !projectId || !lotId) return;
    setCapturing(true);
    try {
      apiRef.current.setIsoCamera();
      await new Promise((r) => setTimeout(r, 120));
      const dataUrl = apiRef.current.capture();
      const job = await createDeterministicWhitebox({ projectId, lotId, vueAngle: 'Aérienne isométrique 45°', imageBase64: dataUrl });
      toast.success('Capture 3D ancrée comme maquette blanche', { description: `Rendu ${job.job_id} · référence d'étape 1 du lot — étape 2 (Texturer) débloquée.` });
    } catch (e) {
      toast.error('Capture impossible', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setCapturing(false);
    }
  };

  const floor = useMemo(() => {
    const imgW = dims?.w || 1000;
    const imgH = dims?.h || 1000;
    const scale = calibration?.scale_m_per_norm_unit
      ? calibration.scale_m_per_norm_unit / ((imgW + imgH) / 2)
      : undefined;

    const pxWalls = (walls || []).map((w) => ({
      id: String(w.id || ''),
      x1: (+w.x1 || 0) * imgW, y1: (+w.y1 || 0) * imgH,
      x2: (+w.x2 || 0) * imgW, y2: (+w.y2 || 0) * imgH,
      thickness: +w.thickness || 8,
      kind: String(w.kind || w.type || 'cloison_fine'),
      ...(Number.isFinite(+w.height_override_m) && +w.height_override_m > 0
        ? { height_override_m: +w.height_override_m } : {}),
      ...(Number.isFinite(+w.low_height_m) && +w.low_height_m > 0 ? { low_height_m: +w.low_height_m } : {}),
      ...(w.railing_style ? { railing_style: w.railing_style } : {}),
      ...(Number.isFinite(+w.bar_depth_m) && +w.bar_depth_m > 0 ? { bar_depth_m: +w.bar_depth_m } : {}),
    }));

    const doors = [];
    const windows = [];
    for (const o of openings || []) {
      const w = (walls || []).find((x) => x.id === o.wall_id);
      if (!w) continue;
      const dx = w.x2 - w.x1;
      const dy = w.y2 - w.y1;
      const len2 = dx * dx + dy * dy;
      if (len2 < 1e-9) continue;
      const t = clamp01(((o.x - w.x1) * dx + (o.y - w.y1) * dy) / len2);
      const rec = {
        wallId: String(w.id || ''),
        t,
        width: (+o.width || 0.08) / Math.sqrt(len2),
      };
      if (String(o.type || '').includes('door')) doors.push(rec);
      else windows.push(rec);
    }

    const stairsPx = (stairs || []).map((s) => ({
      x: (+s.x || 0) * imgW, y: (+s.y || 0) * imgH,
      w: (+s.w || 0.08) * imgW, h: (+s.h || 0.14) * imgH,
    }));

    const fixturesPx = (fixtures || []).map((f) => ({
      kind: f.kind, height_m: +f.height_m || 0, rotation_deg: +f.rotation_deg || 0,
      x: (+f.x || 0) * imgW, y: (+f.y || 0) * imgH, w: (+f.w || 0.05) * imgW, h: (+f.h || 0.05) * imgH,
    }));

    const terrain = (terrainPolygon || []).map((v) => {
      const x = (Array.isArray(v) ? v[0] : v?.x) || 0;
      const y = (Array.isArray(v) ? v[1] : v?.y) || 0;
      return { x: x * imgW, y: y * imgH };
    });

    return {
      hsp_m: hspM,
      geometry: {
        walls: pxWalls, windows, doors, stairs: stairsPx, fixtures: fixturesPx, terrain,
        ...(scale ? { scale_m_per_px: scale } : {}),
      },
    };
  }, [walls, openings, stairs, fixtures, terrainPolygon, calibration, dims, hspM]);

  return (
    <div className="absolute inset-0" data-testid="structure-3d-preview-wrapper">
      <Shoebox3DViewer
        floor={floor}
        className="w-full h-full absolute inset-0"
        testId="structure-3d-preview"
        whiteboxMode={whitebox}
        onReady={(api) => { apiRef.current = api; }}
      />
      <div className="absolute top-2 right-2 flex items-center gap-1.5" data-testid="structure-3d-whitebox-tools">
        <button
          type="button"
          onClick={() => setWhitebox((v) => !v)}
          className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded font-semibold"
          style={{ background: whitebox ? '#E8F1F8' : 'rgba(0,0,0,0.55)', color: whitebox ? '#0B1220' : '#E5E7EB', border: '1px solid rgba(255,255,255,0.25)' }}
          title="Affiche la structure en maquette blanche (tout blanc, fond gris clair)"
          data-testid="structure-3d-whitebox-toggle"
        >
          <Boxes className="w-3 h-3" /> Maquette blanche
        </button>
        {whitebox && projectId && lotId && (
          <button
            type="button"
            onClick={captureWhitebox}
            disabled={capturing || !walls?.length}
            className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded font-semibold disabled:opacity-50"
            style={{ background: 'var(--c-gold, #D9A05B)', color: '#0B1220', border: '1px solid rgba(255,255,255,0.35)' }}
            title="Cadre la caméra en iso 45° et envoie la capture WebGL comme maquette blanche de référence (option haute qualité)"
            data-testid="structure-3d-whitebox-capture"
          >
            {capturing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Capturer → référence étape 1
          </button>
        )}
      </div>
      <div
        className="absolute top-2 left-2 flex items-center gap-1.5 text-[10.5px] px-2 py-1 rounded pointer-events-none"
        style={{ background: 'rgba(0,0,0,0.55)', color: '#5EEAD4', border: '1px solid rgba(94,234,212,0.3)' }}
        data-testid="structure-3d-hint"
      >
        <Rotate3d className="w-3 h-3" />
        Vue 3D temps réel — repassez en 2D pour éditer · molette = zoom · glisser = orbite
      </div>
      {!calibration?.scale_m_per_norm_unit && (
        <div
          className="absolute bottom-2 left-2 text-[10px] px-2 py-1 rounded pointer-events-none"
          style={{ background: 'rgba(0,0,0,0.55)', color: '#FACC15' }}
        >
          ⚠ Non calibré — échelle approximative
        </div>
      )}
    </div>
  );
};

export default Structure3DPreview;
